import { useState, useEffect } from 'react';
import Modal from './Modal';

/**
 * Magic Word Settings — modal for configuring auto-save trigger words and
 * other magic-word shortcuts.
 *
 * Currently houses the block auto-save magic word (trigger word + delay).
 * Future magic-word features (e.g. template shortcuts, command triggers)
 * will be added here.
 *
 * Props:
 *  - show               : boolean
 *  - onClose            : () => void
 *  - blockAutoSaveWord  : string  — current trigger word
 *  - blockAutoSaveDelay : number  — current delay in seconds
 *  - onSaveBlockSettings: (word: string, delay: number) => Promise<void>
 */
export default function MagicWordSettingsModal({
  show,
  onClose,
  blockAutoSaveWord = 'over',
  blockAutoSaveDelay = 3,
  onSaveBlockSettings,
}) {
  // Local form state (doesn't affect live settings until saved)
  const [formWord, setFormWord] = useState(blockAutoSaveWord);
  const [formDelay, setFormDelay] = useState(String(blockAutoSaveDelay));
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setFormWord(blockAutoSaveWord);
      setFormDelay(String(blockAutoSaveDelay));
      setSaving(false);
    }
  }, [show, blockAutoSaveWord, blockAutoSaveDelay]);

  // ESC to close
  useEffect(() => {
    if (!show) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [show, onClose]);

  const handleSave = async () => {
    const word = formWord.trim() || 'over';
    const delay = Math.max(0.5, Math.min(10, parseFloat(formDelay) || 1.5));
    setSaving(true);
    try {
      await onSaveBlockSettings?.(word, delay);
      onClose();
    } catch {
      // parent handles the toast
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      title="Magic Word Settings"
      onClose={onClose}
      modalClass="modal-fixed"
      footer={
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || !formWord.trim()}
          style={{ width: '100%' }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      }
    >
      {/* Block Auto-Save */}
      <div className="ds-step">
        <div>
          <label className="ds-label">Magic word</label>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)', lineHeight: 'var(--leading)', margin: 'var(--space-xs) 0 var(--space-md) 0' }}>
            When editing a block, ending the text with this word enters a waiting-to-confirm
            mode — a progress bar and <strong>Save</strong> / <strong>Close</strong> buttons appear at the top. Press
            <strong>Save</strong> to save immediately, <strong>Close</strong> to cancel, type anything else to keep editing,
            or wait for the countdown to auto-save. The magic word is stripped from the saved content.
          </p>
          <input
            type="text"
            className="ds-input"
            value={formWord}
            onChange={(e) => setFormWord(e.target.value)}
            placeholder="over"
            maxLength={20}
          />
        </div>

        <div style={{ marginTop: 'var(--space-lg)' }}>
          <label className="ds-label">Delay (seconds)</label>
          <input
            type="number"
            className="ds-input"
            value={formDelay}
            onChange={(e) => setFormDelay(e.target.value)}
            placeholder="3"
            min="0.5"
            max="10"
            step="0.1"
            style={{ marginTop: 'var(--space-xs)' }}
          />
        </div>
      </div>
    </Modal>
  );
}
